import React, { useState, useEffect, useCallback } from 'react';
import { format, subDays, parseISO } from 'date-fns';
import { Download, FileText } from 'lucide-react';
import ReportLayout, { exportCSV } from './ReportLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const fmtDate = v => { try { return v ? format(parseISO(v), 'd MMM yyyy') : '–'; } catch { return v||'–'; } };

const SECTIONS = [
  { key:'incidents',  label:'Incidents',   description:'Incident records in date range' },
  { key:'care_plans', label:'Care Plans',  description:'Active and historical care plans' },
  { key:'goals',      label:'Goals',       description:'Care goals and progress tracking' },
  { key:'billings',   label:'Billings',    description:'Invoices and fund transactions' },
  { key:'medications',label:'Medications', description:'Current active medications' },
];

function SectionTable({ title, rows, keys }) {
  if (!rows?.length) return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <p className="font-semibold text-slate-700 mb-3">{title}</p>
      <p className="text-sm text-slate-400">No data available</p>
    </div>
  );
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
        <p className="font-semibold text-slate-700 text-sm">{title}</p>
        <button onClick={() => exportCSV(rows, title.toLowerCase().replace(/\s+/g,'_'))}
          className="flex items-center gap-1.5 text-xs text-primary-600 hover:text-primary-700 font-medium">
          <Download size={12}/> Export
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr>
              {keys.map(k => (
                <th key={k} className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 whitespace-nowrap capitalize">
                  {k.replace(/_/g,' ')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {rows.map((row, i) => (
              <tr key={i} className="hover:bg-slate-50/50">
                {keys.map(k => (
                  <td key={k} className="py-2.5 px-4 text-xs text-slate-700 whitespace-nowrap max-w-[200px] truncate">
                    {row[k] == null ? '–' : typeof row[k] === 'boolean' ? (row[k] ? 'Yes' : 'No') :
                     k.includes('date') && row[k] ? fmtDate(row[k]) :
                     k.includes('amount') || k.includes('total') ? `$${parseFloat(row[k]).toLocaleString()}` :
                     String(row[k])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ClientFullReport() {
  const [clients, setClients]     = useState([]);
  const [clientId, setClientId]   = useState('');
  const [selected, setSelected]   = useState(['incidents','care_plans','goals','billings','medications']);
  const [startDate, setStart]     = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEnd]         = useState(format(new Date(), 'yyyy-MM-dd'));
  const [data, setData]           = useState({});
  const [loading, setLoading]     = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [generated, setGenerated] = useState(false);

  useEffect(() => {
    api.get('/clients').then(r => setClients(Array.isArray(r.data) ? r.data : r.data?.clients||[])).catch(()=>{});
  }, []);

  const toggleSection = key => {
    setSelected(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const generate = () => {
    if (!clientId) { toast.error('Please select a client'); return; }
    if (!selected.length) { toast.error('Please select at least one section'); return; }
    setLoading(true);
    const p = new URLSearchParams({ client_id: clientId, start_date: startDate, end_date: endDate, sections: selected.join(',') });
    api.get(`/reports/client-full-report?${p}`)
      .then(r => { setData(r.data||{}); setGenerated(true); })
      .catch(() => toast.error('Failed to generate report'))
      .finally(() => setLoading(false));
  };

  const selClient = clients.find(c => c.id === clientId);
  const chips = [
    ...(selClient ? [{ label:'Client', value:`${selClient.first_name} ${selClient.last_name}` }] : []),
    { label:'Date Range', value:`${startDate} - ${endDate}` },
  ];

  const allData = Object.values(data).flat();

  return (
    <ReportLayout title="Client Full Report" data={allData} filename="client_full_report"
      activeChips={chips} showFiltersPanel={showFilters} onToggleFilters={() => setShowFilters(v=>!v)}
      filters={
        <div className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <div><label className="block text-xs font-semibold text-slate-500 mb-1">Client *</label>
              <select value={clientId} onChange={e => setClientId(e.target.value)} className="input-field !w-56 text-sm">
                <option value="">Select a client...</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
              </select></div>
            <div><label className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
              <input type="date" value={startDate} onChange={e => setStart(e.target.value)} className="input-field !w-auto text-sm"/></div>
            <div><label className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
              <input type="date" value={endDate} onChange={e => setEnd(e.target.value)} className="input-field !w-auto text-sm"/></div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-2">Sections to Include</label>
            <div className="flex flex-wrap gap-2">
              {SECTIONS.map(s => (
                <button key={s.key} onClick={() => toggleSection(s.key)}
                  className={clsx('flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-medium transition-all',
                    selected.includes(s.key)
                      ? 'bg-primary-50 border-primary-200 text-primary-700'
                      : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50')}>
                  <div className={clsx('w-3.5 h-3.5 rounded border flex items-center justify-center',
                    selected.includes(s.key) ? 'bg-primary-600 border-primary-600' : 'border-slate-300')}>
                    {selected.includes(s.key) && <span className="text-white text-[8px] font-bold">✓</span>}
                  </div>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <button onClick={generate} disabled={loading || !clientId}
            className="btn-primary text-sm px-5 py-2 flex items-center gap-2 disabled:opacity-50">
            {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/> : <FileText size={14}/>}
            Generate Report
          </button>
        </div>
      }>

      {generated && (
        <div className="space-y-5">
          {selClient && (
            <div className="bg-gradient-to-r from-primary-50 to-blue-50 border border-primary-100 rounded-2xl p-5">
              <h2 className="text-lg font-bold text-slate-900">{selClient.first_name} {selClient.last_name}</h2>
              <p className="text-sm text-slate-500 mt-1">Full report covering {startDate} to {endDate} • Sections: {selected.join(', ')}</p>
            </div>
          )}

          {selected.includes('incidents') && (
            <SectionTable title="Incidents" rows={data.incidents||[]}
              keys={['ticket_name','incident_type','incident_date','status','priority']}/>
          )}
          {selected.includes('care_plans') && (
            <SectionTable title="Care Plans" rows={data.care_plans||[]}
              keys={['plan_name','status','start_date','review_date','num_goals']}/>
          )}
          {selected.includes('goals') && (
            <SectionTable title="Goals" rows={data.goals||[]}
              keys={['goal_title','category','progress','status','target_date']}/>
          )}
          {selected.includes('billings') && (
            <SectionTable title="Billings" rows={data.billings||[]}
              keys={['invoice_number','invoice_date','total_amount','status','fund_name']}/>
          )}
          {selected.includes('medications') && (
            <SectionTable title="Medications" rows={data.medications||[]}
              keys={['medication_name','dosage','frequency','route','start_date']}/>
          )}
        </div>
      )}

      {!generated && !loading && (
        <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center">
          <FileText size={40} className="text-slate-200 mx-auto mb-3"/>
          <p className="text-slate-400 text-sm">Select a client and sections, then click Generate Report</p>
        </div>
      )}
    </ReportLayout>
  );
}
